<script setup lang="ts">
import FinestCheckbox from '@/components/FinestCheckbox.vue'
import FinestInputNumber from '@/components/FinestInputNumber.vue'
import FinestInputText from '@/components/FinestInputText.vue'
import FinestSelect from '@/components/FinestSelect.vue'
import {
  isNumericFieldType,
  toDate,
  toIsoDate,
  toYearDate,
  type FieldInputProps,
  type FieldValue,
} from '@/utils/field-value'
import { primeInputFocus } from '@/utils/prime-input-classes'
import DatePicker from 'primevue/datepicker'
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'

const props = withDefaults(defineProps<FieldInputProps>(), {
  options: () => [],
  placeholder: '',
  maxFractionDigits: 2,
  useGrouping: true,
})

const model = defineModel<FieldValue>({ default: null })

const emit = defineEmits<{
  commit: []
}>()

const { t, locale } = useI18n({ useScope: 'local' })

const isNumeric = computed(() => isNumericFieldType(props.type))

const minFractionDigits = computed(() => props.fractionDigits ?? 0)

const maxFractionDigits = computed(() => props.fractionDigits ?? props.maxFractionDigits)

const numberSuffix = computed(() => props.suffix ?? (props.type === 'percent' ? ' %' : ''))

const booleanValue = computed(() => (typeof model.value === 'boolean' ? model.value : null))

function toggleBoolean(next: boolean | null): void {
  model.value = next
  emit('commit')
}
</script>

<template>
  <FinestCheckbox
    v-if="type === 'boolean'"
    :model-value="booleanValue"
    :aria-label="label"
    :state="state"
    @update:model-value="toggleBoolean" />

  <FinestSelect
    v-else-if="type === 'select'"
    :model-value="model"
    :options="options"
    :placeholder="placeholder || t('selectPlaceholder')"
    :aria-label="label"
    :state="state"
    class="w-full"
    @update:model-value="(next) => (model = next ?? null)"
    @change="emit('commit')" />

  <DatePicker
    v-else-if="type === 'date'"
    :model-value="toDate(model)"
    :placeholder="placeholder"
    :aria-label="label"
    :pt="{ pcInputText: { root: { class: primeInputFocus } } }"
    fluid
    show-icon
    icon-display="input"
    date-format="dd.mm.yy"
    @update:model-value="(next) => (model = toIsoDate((next as Date) ?? null))" />

  <DatePicker
    v-else-if="type === 'year'"
    :model-value="toYearDate(model)"
    :placeholder="placeholder"
    :aria-label="label"
    :pt="{ pcInputText: { root: { class: primeInputFocus } } }"
    fluid
    view="year"
    date-format="yy"
    @update:model-value="(next) => (model = (next as Date | null)?.getFullYear() ?? null)" />

  <FinestInputNumber
    v-else-if="isNumeric"
    :model-value="(model as number) ?? null"
    :mode="type === 'currency' ? 'currency' : 'decimal'"
    :min-fraction-digits="minFractionDigits"
    :max-fraction-digits="maxFractionDigits"
    :use-grouping="useGrouping"
    :suffix="numberSuffix"
    :locale="locale"
    :placeholder="placeholder"
    :aria-label="label"
    :state="state"
    class="w-full"
    @update:model-value="(next) => (model = next)" />

  <FinestInputText
    v-else
    :model-value="(model as string) ?? ''"
    :placeholder="placeholder"
    :aria-label="label"
    :state="state"
    class="w-full"
    @update:model-value="(next) => (model = next ?? null)" />
</template>

<i18n lang="json">
{
  "de": {
    "selectPlaceholder": "Bitte wählen …"
  },
  "en": {
    "selectPlaceholder": "Please select …"
  }
}
</i18n>
